import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { patchBmsSettings, type BmsSettingsPatch } from "@client/api/bms";
import { t } from "@client/lib/dictionary";
import { showWriteResults } from "@client/lib/verifyToast";
import { useUserStore } from "@client/store/user";
import type { BmsSettings } from "@client/types/bms";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@client/components/ui/alert-dialog";
import { Button } from "@client/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@client/components/ui/card";
import { Input } from "@client/components/ui/input";
import { Label } from "@client/components/ui/label";

interface Props {
  slug: string;
  settings: BmsSettings | null | undefined;
}

const fields: Array<{ key: keyof BmsSettingsPatch; label: string }> = [
  { key: "cellOvpMv", label: t.bms.fields.cellOvp },
  { key: "cellOvrMv", label: t.bms.fields.cellOvr },
  { key: "cellUvpMv", label: t.bms.fields.cellUvp },
  { key: "cellUvrMv", label: t.bms.fields.cellUvr },
  { key: "balanceTriggerDeltaMv", label: t.bms.fields.balanceDelta },
  { key: "balanceStartMv", label: t.bms.fields.balanceStart },
  { key: "chargeOcpA", label: t.bms.fields.chargeOcp },
  { key: "dischargeOcpA", label: t.bms.fields.dischargeOcp },
];

/** Popisky polí pro ověřovací toasty (mapuje field klíč z WriteFieldResult). */
const FIELD_LABELS: Record<string, string> = Object.fromEntries(fields.map((f) => [f.key, f.label]));

export const BmsSettingsForm: React.FC<Props> = ({ slug, settings }) => {
  const user = useUserStore((s) => s.user);
  const isAdmin = typeof user === "object" && user !== null && (user as { role: string }).role === "ADMIN";
  const [form, setForm] = useState<BmsSettingsPatch>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Sync settings snapshotu z BMS do form state. Settings stream přichází ~30 s
  // ze socketu — overwrite je tady záměr (form je defaultně in-sync s BMS).
  useEffect(() => {
    if (!settings) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setForm({
      cellOvpMv: settings.cellOvpMv,
      cellOvrMv: settings.cellOvrMv,
      cellUvpMv: settings.cellUvpMv,
      cellUvrMv: settings.cellUvrMv,
      balanceTriggerDeltaMv: settings.balanceTriggerDeltaMv,
      balanceStartMv: settings.balanceStartMv,
      chargeOcpA: settings.chargeOcpA,
      dischargeOcpA: settings.dischargeOcpA,
    });
  }, [settings]);

  if (!isAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t.bms.settings}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">{t.bms.adminRequired}</CardContent>
      </Card>
    );
  }

  // BMS legacy protokol může na některých firmwarech read-individual-register
  // ignorovat. Driver v takovém případě settings event NEemituje (settings
  // zůstanou null) — UI ukáže waiting message místo zer (mirror PIP/TRACER).
  if (!settings) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t.bms.settings}</CardTitle>
          <p className="text-xs text-amber-600">⚠ {t.bms.settingsWarning}</p>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground italic">
          Čekám na settings z BMS… (po připojení trvá ~3 s; pokud zůstane natrvalo, BMS firmware
          nepodporuje `5A 5A` read protocol — viz pm2 log{" "}
          <code className="font-mono not-italic">refreshSettings: sweep DONE</code>).
        </CardContent>
      </Card>
    );
  }

  const changed = (key: keyof BmsSettingsPatch): boolean => {
    if (!settings) return false;
    return form[key] !== (settings[key as keyof BmsSettings] as number);
  };

  const dirtyFields = fields.filter((f) => changed(f.key));

  const submit = async () => {
    setBusy(true);
    try {
      const payload: BmsSettingsPatch = {};
      for (const f of dirtyFields) {
        payload[f.key] = form[f.key] as number;
      }
      const res = await patchBmsSettings(slug, payload);
      // Hodnoty pochází z reálného read-backu z BMS (žádné dosazení na FE).
      showWriteResults(res.responseObject?.results ?? [], FIELD_LABELS);
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg ?? String(err));
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.bms.settings}</CardTitle>
        <p className="text-xs text-red-600">⚠ {t.bms.settingsWarning}</p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fields.map((f) => (
            <div key={f.key} className="flex flex-col gap-1">
              <Label htmlFor={f.key}>{f.label}</Label>
              <Input
                id={f.key}
                type="number"
                value={(form[f.key] as number | undefined) ?? ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, [f.key]: e.target.value === "" ? undefined : Number(e.target.value) }))
                }
                className={changed(f.key) ? "border-amber-500" : ""}
              />
              {settings && (
                <span className="text-xs text-muted-foreground">
                  Aktuálně: {settings[f.key as keyof BmsSettings] as number}
                </span>
              )}
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={() => setConfirmOpen(true)} disabled={dirtyFields.length === 0 || busy}>
            {t.bms.actions.saveSettings} ({dirtyFields.length})
          </Button>
        </div>
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={(o) => !busy && setConfirmOpen(o)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.bms.confirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="mb-2">Zapsat tato nastavení na BMS?</div>
              <ul className="text-xs space-y-1">
                {dirtyFields.map((f) => (
                  <li key={f.key} className="flex justify-between gap-2 font-mono">
                    <span>{f.label}</span>
                    <span>
                      {settings ? (settings[f.key as keyof BmsSettings] as number) : "?"} → {form[f.key] as number}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-xs text-amber-600">⚠ {t.bms.settingsWarning}</div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{t.app.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={submit} disabled={busy}>
              {busy ? t.app.loading : t.bms.confirmYes}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
